import useLocalStorage from '@rehooks/local-storage';
import { useQuery } from 'react-query';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import type {
  AppsV1IdentityResponse,
  GenesisUserInfoResponse,
  Identity,
} from '../types';
import { fetchWrapper, formatLocalStorageKey } from '../utils';

export const IDENTITY_QUERY_KEY = 'ZNavIdentity';

/** `one.*` hosts (Zuora One ID); billing and other stacks use different hostnames. */
export function isOneIdHostname(): boolean {
  return window.location.hostname.startsWith('one');
}

/**
 * Whether identity fetch should run.
 * Always allowed when the hook is enabled — including OneID.
 *
 * On `one.*`, do not gate on readable cookies: `ZUNISESSION` is HttpOnly.
 * Browser fetch still sends it same-origin. Gateway should route
 * `/platform/gateway-proxy-v2/v1/identity` (+ genesis) to OneID.
 */
export function isIdentityFetchAllowed(): boolean {
  return true;
}

/** Validates cached identity from localStorage; returns undefined if invalid/corrupt so we don't use as initialData. */
function getValidCachedIdentity(
  cached: Partial<Identity> | null | undefined,
): Partial<Identity> | undefined {
  if (cached == null || typeof cached !== 'object' || Array.isArray(cached)) {
    return undefined;
  }
  return cached;
}

/**
 * Shared platform identity — same URLs on Billing and OneID.
 * On `one.*`, gateway must route these to Universal Identity (GEN-11565),
 * not Billing.
 */
async function fetchPlatformIdentity(): Promise<Identity> {
  const [billingIdentity, genesisUserInfo] = await Promise.all([
    // For roles and basic user info (ex: language)
    fetchWrapper<AppsV1IdentityResponse>(NAVIGATION_ENDPOINTS.V1_IDENTITY, {
      credentials: 'include',
    }),
    // For permissions and (accessible) entities
    fetchWrapper<GenesisUserInfoResponse>(
      NAVIGATION_ENDPOINTS.GENESIS_USER_INFO,
      { credentials: 'include' },
    ),
  ]);

  return {
    ...genesisUserInfo,
    platformRole: billingIdentity.platformRole,
    insightsRole: billingIdentity.insightsRole,
    globalTenantStatus: billingIdentity.tenantStatus,
    globalTenantTrialEndDate: billingIdentity.tenantTrialEndDate,
    jobTitle: billingIdentity.jobTitle,
    jobFunctions: billingIdentity.jobFunctions,
    entityId: billingIdentity.entityId,
    userId: billingIdentity.userId,
    userRoleMap: billingIdentity.userRoleMap,
    userProfileId: billingIdentity.userProfileId,
    email: billingIdentity.email,
  };
}

// GET /platform/gateway-proxy-v2/v1/identity + genesis/user/info
export const useIdentity = ({
  enabled = true,
  queryKey = IDENTITY_QUERY_KEY,
}: { enabled?: boolean; queryKey?: string } = {}) => {
  const [cachedIdentity, setCachedIdentity] = useLocalStorage<
    Partial<Identity>
  >(formatLocalStorageKey('ZNavIdentity'));

  const validCache = getValidCachedIdentity(cachedIdentity);
  const queryEnabled = enabled && isIdentityFetchAllowed();
  const oneIdHost = isOneIdHostname();

  const queryResult = useQuery<
    Partial<Identity & { hasPermission: (...permissions: string[]) => boolean }>
  >(queryKey, () => fetchPlatformIdentity(), {
    initialData: () => {
      if (validCache) {
        return validCache;
      }
    },
    onSuccess: (data) => {
      setCachedIdentity(data);
    },
    /* One ID only: many observers + staleTime 0 + refetchOnMount caused unbounded refetches on
     * repeated failure (e.g. 401). Billing hosts keep prior remount refetch behavior for resilience. */
    ...(oneIdHost
      ? {
          refetchOnMount: false,
          retryOnMount: false,
          refetchOnReconnect: false,
          staleTime: 5 * 60 * 1000,
        }
      : {
          refetchOnMount: true,
          staleTime: 0,
        }),
    enabled: queryEnabled,
  });

  // OneID: failed identity (e.g. gateway not routed yet) → null, not stale cache alone.
  const data = oneIdHost && queryResult.isError ? null : queryResult.data;

  return {
    ...queryResult,
    data,
    // App Builder defines a permissions type that should be used, but we shouldn't duplicate it here.
    // TODO: Create an external service(?) to return the type IdentityPermissions to use both here and in app builder.
    hasPermission: (...permissions: string[]) => {
      /* One ID: until/unless genesis permissions are authoritative, Concierge is gated by
       * /api/configs + page allowlist. Always allow on OneID so default variant=billing
       * does not hide the AI launcher via useAiPanelHidden when identity is empty/error. */
      if (isOneIdHostname()) {
        return true;
      }
      return permissions.every((permission) =>
        data?.permissions?.includes(permission),
      );
    },
  };
};

export const useCurrentIdentity = () => {
  const { data } = useIdentity();

  return {
    locale: data?.locale || 'en_US',
    tenantTimeZone: data?.tenantTimeZone || 'UTC',
  };
};
